import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '@/lib/queryClient';
import { FRONTEND_VERSION, isVersionOlder } from '@/lib/version';

const STORAGE_KEY = 'whatsNewSeenVersion';

interface WhatsNewConfig {
  whatsNewVersion: string;
  whatsNewContentFr: string;
  whatsNewContentEn: string;
  whatsNewContentEs: string;
}

export function WhatsNewModal() {
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');
  const { i18n, t } = useTranslation();

  useEffect(() => {
    async function check() {
      try {
        if (!localStorage.getItem('authToken')) return;

        const response = await apiRequest('GET', '/api/config');
        if (!response.ok) return;

        const config: WhatsNewConfig = await response.json();
        if (!config.whatsNewVersion) return;
        if (localStorage.getItem(STORAGE_KEY) === config.whatsNewVersion) return;
        // App pas encore mise à jour (ex. 1.49 installée, popup 1.50) : on attend la mise à jour
        if (isVersionOlder(FRONTEND_VERSION, config.whatsNewVersion)) return;

        const lang = i18n.language?.slice(0, 2).toLowerCase();
        let text = config.whatsNewContentFr;
        if (lang === 'en') text = config.whatsNewContentEn || config.whatsNewContentFr;
        else if (lang === 'es') text = config.whatsNewContentEs || config.whatsNewContentFr;

        if (!text) return;

        setVersion(config.whatsNewVersion);
        setContent(text);
        setVisible(true);
      } catch {
        // silent fail
      }
    }

    check();
  }, [i18n.language]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, version);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9998] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card shadow-2xl rounded-2xl p-6 max-w-sm w-full border border-border text-center space-y-5 animate-in fade-in zoom-in duration-300">
        <div className="mx-auto w-16 h-16 bg-primary/15 text-primary rounded-full flex items-center justify-center">
          <Sparkles size={30} />
        </div>

        <h2 className="text-xl font-bold tracking-tight text-foreground">
          {t('whats_new.title', 'Nouveautés v{{version}}', { version })}
        </h2>

        <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line text-left">
          {content}
        </p>

        <button
          onClick={dismiss}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-95"
        >
          {t('whats_new.dismiss', 'Super, merci !')}
        </button>
      </div>
    </div>
  );
}
